import {
  buffers, type Channel,
} from 'redux-saga';
import {
  actionChannel,
  all,
  call, put, take,
  takeEvery,
} from 'redux-saga/effects';
import { SelectedDataType } from '~/_shared/components/customizeMetrics/selectedDataMetric.type';
import { CONFIG } from '~/_shared/constants/config';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { notEmpty } from '~/_shared/utils/array/array.helpers';
import type { MetricModel } from '~/_shared/utils/metric/metrics.factory';
import { select } from '~/_shared/utils/saga/effects';
import { notNullOrUndefined } from '~/_shared/utils/typeGuards';
import { createBoundaryTerritoryGroupRequestParams } from '~/sidebar/sidebarApps/mapTools/boundary/boundaryTerritoryGroup.helpers';
import { createDataLayerDemographicBTGRequestParams } from '~/sidebar/sidebarApps/mapTools/dataLayersTool/dataLayersTool.helpers';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import {
  BoundaryTerritoryGroupConnectedEntities, BoundaryTerritoryType,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import { boundaryTerritoryGroupsRemoveCompleted } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import {
  createOfflineBoundaryTerritoryGroup, updateOfflineBoundaryTerritoryGroups,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.sagas';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { type boundaryToolBTGsSelector } from '~/store/boundaryTerritoryGroups/boundaryTool/boundaryToolBTGs.selectors';
import { dataLayersBTGsSelector } from '~/store/boundaryTerritoryGroups/dataLayersTool/dataLayersBTGs.selectors';
import { isBTGForDataLayerDemographic } from '~/store/boundaryTerritoryGroups/dataLayersTool/dataLayersToolBTGs.helpers';
import { waitForPremiumDemographicsReady } from '~/store/demographics/premiumDemographics/demographicsListPremium.sagas';
import {
  activePremiumDemographicsAllSelector,
  demographicsListPremiumActiveSelector,
} from '~/store/demographics/premiumDemographics/demographicsListPremium.selectors';
import { mapToolChanged } from '~/store/frontendState/leftSidebar/leftSidebar.actionCreators';
import {
  dataLayersToolBoundaryTerritoryGroupsCreated, dataLayersToolUpdateFinished, dataLayersToolUpdateRequest,
  dataLayersToolUpdateStarted,
} from '~/store/frontendState/mapTools/dataLayers/dataLayers.actionCreators';
import {
  mapSettingsDataLayersAddDataLayer, mapSettingsDataLayersRemoveDataLayer, mapSettingsDataLayersUpdateBoundaryGroup, mapSettingsDataLayersUpdateDataLayer,
} from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.actionCreators';
import {
  mapSettingsActiveDataLayerSelector, mapSettingsDataLayersPrimaryBoundaryGroupIdSelector,
} from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.selectors';
import type { DataLayer } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.state';
import { waitForMapSettingsReady } from '~/store/mapSettings/ready/mapSettingsReady.sagas.helpers';
import { clientIdSelector } from '~/store/selectors/useClientIdSelector';
import { mapIdSelector } from '~/store/selectors/useMapIdSelector';

export function* mapSettingsDataLayersSaga() {
  if (CONFIG.ENABLE_DATA_LAYERS) {
    yield takeEvery([
      mapSettingsDataLayersAddDataLayer,
      mapSettingsDataLayersRemoveDataLayer,
      mapSettingsDataLayersUpdateDataLayer,
      mapSettingsDataLayersUpdateBoundaryGroup,
    ], onDataLayersChanged);
    yield takeEvery(mapToolChanged, onMapToolChanged);
    yield all([
      watchForDataLayersUpdateRequests(),
    ]);
  }
}

function* onDataLayersChanged() {
  yield put(dataLayersToolUpdateRequest());
}

function* onMapToolChanged(action: ReturnType<typeof mapToolChanged>) {
  const openedTool = action.payload.newTool;

  if (openedTool === MapTool.DataLayers) {
    yield call(waitForMapSettingsReady);
    yield call(waitForPremiumDemographicsReady);
    yield put(dataLayersToolUpdateRequest());
  }
}

function* watchForDataLayersUpdateRequests() {
  const channel: Channel<Action> = yield actionChannel(dataLayersToolUpdateRequest, buffers.sliding(1));

  while (true) {
    yield take(channel);
    try {
      yield call(updateDataLayerBoundaryGroups);
    }
    catch (error) {
      console.error('Error updating Data Layer boundary groups', error);
    }
  }
}

function* updateDataLayerBoundaryGroups() {
  yield put(dataLayersToolUpdateStarted());

  const clientId: ReturnType<typeof clientIdSelector> = yield select(clientIdSelector);
  const mapId: ReturnType<typeof mapIdSelector> = yield select(mapIdSelector);
  const currentDataLayersBTGs: ReturnType<typeof boundaryToolBTGsSelector> = yield select(dataLayersBTGsSelector);
  const boundaryGroupId: BoundaryGroupId | null = yield select(mapSettingsDataLayersPrimaryBoundaryGroupIdSelector);
  const activeDataLayer: DataLayer | null = yield select(mapSettingsActiveDataLayerSelector);
  const activePremiumDemographicsAll: ReturnType<typeof activePremiumDemographicsAllSelector> = yield select(activePremiumDemographicsAllSelector);
  const accessibleDemographics = activePremiumDemographicsAll.accessible;
  const activeDataLayerDemographics = activeDataLayer?.data.demographics;

  const remainingDemographics = activeDataLayerDemographics ? [...activeDataLayerDemographics] : [];

  const activeDataLayersBTGs = activeDataLayer
    ? currentDataLayersBTGs.filter(btg => {
      const matchIndex = remainingDemographics.findIndex(demographic =>
        isBTGForDataLayerDemographic({
          boundaryGroupId,
          dataLayerId: activeDataLayer.id,
          demographic,
          boundaryTerritoryGroup: btg,
          mapId,
          numberOfRanges: activeDataLayer.settings.numberOfRanges,
          gradientColors: activeDataLayer.settings.style.gradientColors,
        })
      );
      if (matchIndex !== -1) {
        // each demographic should only match one BTG, so we can remove it
        // from the remainingDemographics array to prevent it from matching another BTG
        remainingDemographics.splice(matchIndex, 1);
        return true;
      }
      return false;
    })
    : [];

  // the following section removes BTGs created for the Data Layers Tool that are no longer needed
  const currentDataLayersBTGsToRemove = currentDataLayersBTGs.filter(btg => !activeDataLayersBTGs.includes(btg));
  yield put(boundaryTerritoryGroupsRemoveCompleted(currentDataLayersBTGsToRemove));

  if (!mapId || !clientId || !boundaryGroupId || !activeDataLayer) {
    yield put(dataLayersToolUpdateFinished());
    return;
  }

  // the following section updates settings of BTGs which don't require re-creation (e.g. some style changes)
  const updatedBTGs = activeDataLayersBTGs.map(btg => ({
    ...btg,
    settings: {
      ...btg.settings,
      style: activeDataLayer.settings.style,
      disableMapInteraction: activeDataLayer.settings.disableMapInteraction,
      hideLabels: activeDataLayer.settings.hideLabels,
      labelStyle: activeDataLayer.settings.labelStyle,
      labelValueConfig: activeDataLayer.settings.labelValueConfig,
    },
  }));
  if (notEmpty(updatedBTGs)) {
    yield call(
      updateOfflineBoundaryTerritoryGroups,
      updatedBTGs,
      {
        fetchBoundaryDetails: false,
        failSilently: true,
      },
      clientId,
    );
  }

  const dataLayerBoundaryGroupCommonSettings = {
    style: {
      line_width: activeDataLayer.settings.style.lineWidth,
      line_color: activeDataLayer.settings.style.lineColor,
      gradient_colors: activeDataLayer.settings.style.gradientColors,
    },
    numberOfRanges: activeDataLayer.settings.numberOfRanges,
    disableMapInteraction: activeDataLayer.settings.disableMapInteraction,
    hideLabels: activeDataLayer.settings.hideLabels,
    labelStyle: activeDataLayer.settings.labelStyle,
    labelValueConfig: activeDataLayer.settings.labelValueConfig,
  };

  const demographicsToLoadWithoutAccessFilter = activeDataLayerDemographics?.filter(demographic =>
    !activeDataLayersBTGs.some(btg => btg.settings.demographicId === demographic)
  );

  const demographicsToLoad = demographicsToLoadWithoutAccessFilter?.filter(demographic =>
    accessibleDemographics.has(demographic)
  );

  // if there are non-accessible demographics and no existing BTGs, we create a placeholder one
  if (demographicsToLoadWithoutAccessFilter
    && demographicsToLoad
    && demographicsToLoad.length === 0
    && demographicsToLoadWithoutAccessFilter.length !== demographicsToLoad.length
    && !activeDataLayersBTGs.length
  ) {
    yield call(
      createOfflineBoundaryTerritoryGroup,
      [createBoundaryTerritoryGroupRequestParams({
        mapId,
        boundaryGroupId,
        territoryType: BoundaryTerritoryType.Manual,
        connectedEntityData: {
          entity: BoundaryTerritoryGroupConnectedEntities.DataLayer,
          id: activeDataLayer.id,
        },
        ...dataLayerBoundaryGroupCommonSettings,
      })],
      clientId,
    );
    yield put(dataLayersToolUpdateFinished());
    return;
  }

  // the following section creates new BTGs for the demographics
  // of the active data layer that do not have a corresponding BTG yet
  if (!demographicsToLoad || demographicsToLoad.length === 0) {
    yield put(dataLayersToolUpdateFinished());
    return;
  }

  const activeDemographics: ReturnType<typeof demographicsListPremiumActiveSelector> = yield select(demographicsListPremiumActiveSelector);
  const metrics: ReadonlyArray<MetricModel> = activeDemographics.accessible
    .filter(d => demographicsToLoad.includes(d.id))
    .map(d => ({
      type: SelectedDataType.Demographic,
      demographic: d,
    }));

  const requests = demographicsToLoad.map((demographicId) => createDataLayerDemographicBTGRequestParams({
    dataLayerId: activeDataLayer.id,
    mapId,
    boundaryGroupId,
    demographicId,
    metrics,
    ...dataLayerBoundaryGroupCommonSettings,
  }));

  const newBoundaryTerritoryGroups: ReadonlyArray<BoundaryTerritoryGroup> = yield call(
    createOfflineBoundaryTerritoryGroup,
    requests,
    clientId,
  );

  if (newBoundaryTerritoryGroups) {
    const finalBTGs = demographicsToLoad.map((demographicId) => {
      return newBoundaryTerritoryGroups.find(btg => btg.boundaryGroupId === boundaryGroupId && btg.settings.demographicId === demographicId);
    }).filter(notNullOrUndefined);

    yield put(dataLayersToolBoundaryTerritoryGroupsCreated(finalBTGs));
  }
}
